import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "components/ui/button";
import { FaPlay } from "react-icons/fa6";
import { useExecuteQueryMutation } from "@/services/it/updates/query-update";
import { MESSAGES } from "../../utils/messages";
import { toast } from "components/ui/use-toast";
import { QueryResponseData } from "@/@types/query-response";
import { DialogDescription } from "@radix-ui/react-dialog";
import { DataTable } from "../query-request/select/DataTable";
import ErrorTerminal from "../query-request/error-terminal";
import { QueryResultModal } from "../query-request/modal/query-result-modal";

interface DataModalProps {
  query: string;
  data: any;
  connectionId: number;
  onClose: () => void;
}

export const EditDataPreviewModal: React.FC<DataModalProps> = ({
  data,
  query,
  connectionId,
  onClose,
}) => {
  if (!data?.result?.length) {
    return (
      <p className="text-xs p-3 border border-black/20 rounded-md my-2">
        Nenhum dado disponível ou alterado.
      </p>
    );
  }

  const [modalData, setModalData] = useState<QueryResponseData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [executeQuery, { isLoading }] = useExecuteQueryMutation();

  const handleSubmit = async () => {
    try {
      const result = await executeQuery({ query, connectionId }).unwrap();

      if (
        [
          "Comandos do tipo 'TRUNCATE' ou 'DROP' não são permitidos.",
          "Comandos do tipo 'UPDATE' ou 'DELETE' sem 'WHERE' não são permitidos.",
        ].includes(result)
      ) {
        toast(
          result === MESSAGES.QUERY_ERROR.ERROR
            ? MESSAGES.QUERY_ERROR.ERROR
            : MESSAGES.NO_WHERE.ERROR
        );
        return;
      }

      setModalData(result);
      setErrorMessage("");
      toast(MESSAGES.CREATE.SUCCESS);
    } catch (error: any) {
      if (error?.status === 500) {
        setErrorMessage(error?.data?.messages[0]);
      } else {
        console.error("Algo não saiu como esperado!", error);
        setErrorMessage("Erro inesperado ao executar a query.");
      }
      toast(MESSAGES.CREATE.ERROR);
    }
  };

  const rows = data.result;
  const columnNames = Object.keys(rows[0] || {});

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-white">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="text-2xl font-bold">
              Detalhes da Query
            </DialogTitle>
            <DialogDescription>
              Este é um pré-visualização dos dados que sua query irá impactar.
              Tem certeza de que deseja executá-la no banco?
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[calc(90vh-8rem)]">
            <div className="grid gap-6 px-6">
              <DataTable columnNames={columnNames} rows={rows} />
              <ErrorTerminal error={errorMessage} />
            </div>
          </ScrollArea>
          <DialogFooter className="p-4 flex gap-2">
            <Button
              onClick={handleSubmit}
              disabled={isLoading}
              className="bg-black text-white rounded-md font-[550] text-sm p-2 px-4 flex items-center gap-2 hover:bg-black/80 transition-all ease-in-out duration-300"
            >
              <FaPlay />
              {isLoading ? "Carregando..." : "Confirmar Query"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {modalData && <QueryResultModal data={modalData} onClose={onClose} />}
    </>
  );
};
